import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-requests/mine");
      setRequests(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate("/"); return; }
    if (user.role === "admin") { navigate("/admin"); return; }
    load();
  }, [user, load, navigate]);

  if (user === null || user === false) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-xs uppercase tracking-[0.3em] text-neutral-400"
        style={{ background: "radial-gradient(ellipse at 50% 0%, #2a2a2a 0%, #141414 40%, #050505 80%, #000000 100%)" }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div
      data-testid="member-dashboard"
      className="min-h-screen text-neutral-200"
      style={{
        background:
          "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <header
        className="border-b sticky top-0 z-10 backdrop-blur-md"
        style={{ borderColor: "#22252A", background: "rgba(0,0,0,0.72)" }}
      >
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center gap-4">
          <Link to="/member" className="flex items-center gap-3 min-w-0">
            <div>
              <div
                aria-hidden
                className="w-40 h-4"
                style={{
                  background: METAL_BG,
                  WebkitMaskImage: "url(/wellnet-logo.png)",
                  maskImage: "url(/wellnet-logo.png)",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskPosition: "left center",
                  maskPosition: "left center",
                }}
              />
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-1">History</div>
            </div>
          </Link>
          <div className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-[0.25em]">
            <span className="text-neutral-400 hidden sm:inline" data-testid="member-email">{user.email}</span>
            <button
              data-testid="member-logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-neutral-600 text-neutral-300 px-3 py-1.5 hover:bg-neutral-200 hover:text-black transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">Welcome</div>
        <h1
          data-testid="member-welcome"
          className="font-semibold tracking-tight text-neutral-100"
          style={{
            fontFamily: "'Syne','Archivo',sans-serif",
            fontSize: "clamp(29px,4.6vw,44px)",
            lineHeight: 1.1,
          }}
        >
          {user.name || user.email.split("@")[0]}
        </h1>
        <p className="mt-3 max-w-[60ch] text-neutral-400 text-[15.5px]">
          Tell us where and what — your concierge will be in touch to arrange the details.
        </p>

        <Link
          to="/member/request"
          data-testid="request-service-btn"
          className="mt-10 inline-block uppercase tracking-[0.3em] text-xs font-bold py-4 px-8 rounded-md transition-transform hover:-translate-y-0.5"
          style={{ background: METAL_BG, color: "#08090B" }}
        >
          Request a Service →
        </Link>

        <section className="mt-20">
          <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-6">
            <span className="inline-block w-8 h-px align-middle mr-3" style={{ background: METAL_BG }} />
            Your Requests
          </div>

          {error && (
            <div className="text-xs uppercase tracking-[0.2em] px-3 py-2 mb-4"
              style={{ color: "#D9A05B", border: "1px solid #D9A05B" }}>
              {error}
            </div>
          )}
          {loading ? (
            <div className="text-xs uppercase tracking-[0.3em] text-neutral-500">Loading…</div>
          ) : requests.length === 0 ? (
            <div
              data-testid="member-empty"
              className="p-8 text-neutral-500 text-sm rounded-lg"
              style={{ border: "1px solid #22252A", background: "#101114" }}
            >
              No requests yet. Submit your first above.
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden" style={{ border: "1px solid #22252A", background: "#101114" }}>
              {requests.map((r, i) => (
                <div
                  key={r.id}
                  data-testid={`member-request-${r.id}`}
                  className="p-5"
                  style={{ borderTop: i === 0 ? "none" : "1px solid #22252A" }}
                >
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4">
                      <div className="font-black uppercase tracking-tight text-neutral-100">{r.city}</div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                        {r.reference && <span className="ml-2 tabular-nums text-neutral-400">· {r.reference}</span>}
                      </div>
                    </div>
                    <div className="col-span-6 text-sm text-neutral-300">
                      {r.services.join(", ")}
                      {r.duration && <div className="text-neutral-500 text-xs mt-1">Duration: {r.duration}</div>}
                      {r.preferred_date && !r.duration && <div className="text-neutral-500 text-xs mt-1">Preferred: {r.preferred_date}</div>}
                    </div>
                    <div className="col-span-2 text-right">
                      <StatusBadge status={r.status} />
                    </div>
                  </div>

                  {r.concierge_reply && (
                    <div
                      data-testid={`concierge-reply-${r.id}`}
                      className="mt-4 p-4 rounded-lg"
                      style={{
                        background: "rgba(199,202,209,0.04)",
                        border: "1px solid #31353C",
                        boxShadow: "inset 2px 0 0 #C7CAD1",
                      }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span
                          className="text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5"
                          style={{ background: "#C7CAD1", color: "#08090B" }}
                        >
                          Concierge Reply
                        </span>
                        {r.concierge_reply.replied_at && (
                          <span className="text-[11px] text-neutral-500">
                            {new Date(r.concierge_reply.replied_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {(r.concierge_reply.confirmed_venue || r.concierge_reply.confirmed_price || r.concierge_reply.confirmed_datetime) && (
                        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 text-sm">
                          {r.concierge_reply.confirmed_venue && (
                            <div>
                              <dt className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Venue</dt>
                              <dd className="text-neutral-100">{r.concierge_reply.confirmed_venue}</dd>
                            </div>
                          )}
                          {r.concierge_reply.confirmed_price && (
                            <div>
                              <dt className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Price</dt>
                              <dd className="text-neutral-100">{r.concierge_reply.confirmed_price}</dd>
                            </div>
                          )}
                          {r.concierge_reply.confirmed_datetime && (
                            <div>
                              <dt className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Time</dt>
                              <dd className="text-neutral-100">{r.concierge_reply.confirmed_datetime}</dd>
                            </div>
                          )}
                        </dl>
                      )}
                      <div className="text-sm text-neutral-300 whitespace-pre-wrap">{r.concierge_reply.message}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export function StatusBadge({ status }) {
  const styles = {
    pending:     { color: "#C7CAD1", borderColor: "#31353C", bg: "transparent" },
    in_progress: { color: "#F3F4F6", borderColor: "#C7CAD1", bg: "transparent" },
    completed:   { color: "#08090B", borderColor: "transparent", bg: METAL_BG },
    cancelled:   { color: "#6F737B", borderColor: "#31353C", bg: "transparent" },
  };
  const s = styles[status] ?? styles.pending;
  const label = status === "in_progress" ? "in progress" : status;
  return (
    <span
      className="inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em]"
      style={{
        color: s.color,
        border: `1px solid ${s.borderColor}`,
        background: s.bg,
        textDecoration: status === "cancelled" ? "line-through" : "none",
      }}
    >
      {label}
    </span>
  );
}
